import { Add, Delete, KeyboardArrowLeft } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid2,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { listCaptures } from "../../../api/captures";
import { getCaptureById } from "../../../api/captures/capture";
import { CreateFlowTestMeasurementRequest } from "../../../types/model/flowTestMeasurement";
import { VariableResponse } from "../../../types/model/variable";
import { formatTimestamp } from "../../../utils";
import { PaginatedAutocomplete } from "../../General/PaginatedAutocomplete";

interface NewGroupFormProps {
  flowTestId: number;
  captureId?: number;
  entityId: number;
  onCreate: (values: CreateFlowTestMeasurementRequest) => Promise<void>;
  onCancel: () => void;
  variables: VariableResponse[];
}

type ContextFormValues = {
  measuredCaptureId: number | null;
  timeMode: "measuredAt" | "testingTime";
  measuredAt: string;
  testingTime: string;
};

type VarRow = { variableId: number | null; value: string; flag: string; notes: string };
type VarRowErrors = { variableId: boolean; value: boolean };

export const NewGroupForm = ({
  flowTestId,
  captureId,
  entityId,
  onCreate,
  onCancel,
  variables,
}: NewGroupFormProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<"time" | "variables">("time");
  const [varRows, setVarRows] = useState<VarRow[]>([
    { variableId: null, value: "", flag: "", notes: "" },
  ]);
  const [rowErrors, setRowErrors] = useState<VarRowErrors[]>([
    { variableId: false, value: false },
  ]);

  const contextMethods = useForm<ContextFormValues>({
    defaultValues: {
      measuredCaptureId: null,
      timeMode: "testingTime",
      measuredAt: "",
      testingTime: "",
    },
  });

  const { watch: watchCtx, register: registerCtx, handleSubmit: handleCtx, control } = contextMethods;
  const timeMode = watchCtx("timeMode");
  const measuredCapture = watchCtx("measuredCaptureId");

  useEffect(() => {
    contextMethods.reset({
      measuredCaptureId: captureId ?? null,
      timeMode: "testingTime",
      measuredAt: "",
      testingTime: "",
    });
  }, [captureId]);

  const fetchCaptures = useCallback(
    (search: string, offset: number, limit: number) =>
      listCaptures(entityId, undefined, search, undefined, limit, offset, true),
    [entityId]
  );

  const fetchOneCapture = useCallback((id: number) => getCaptureById(id), []);

  const handleStep1Next = handleCtx((data) => {
    if (!data.measuredCaptureId) return;
    if (data.timeMode === "measuredAt" && !data.measuredAt) return;
    if (data.timeMode === "testingTime" && !data.testingTime) return;
    setStep("variables");
  });

  const addVarRow = () => {
    setVarRows((prev) => [...prev, { variableId: null, value: "", flag: "", notes: "" }]);
    setRowErrors((prev) => [...prev, { variableId: false, value: false }]);
  };

  const updateVarRow = (idx: number, field: keyof VarRow, val: string | number | null) => {
    setVarRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
    if (field === "variableId" || field === "value") {
      setRowErrors((prev) =>
        prev.map((e, i) =>
          i === idx ? { ...e, [field]: val === null || val === "" ? e[field] : false } : e
        )
      );
    }
  };

  const removeVarRow = (idx: number) => {
    setVarRows((prev) => prev.filter((_, i) => i !== idx));
    setRowErrors((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    const errors = varRows.map((row) => ({
      variableId: !row.variableId,
      value: !row.value,
    }));
    const hasErrors = errors.some((e) => e.variableId || e.value);
    if (hasErrors) {
      setRowErrors(errors);
      return;
    }

    const ctxData = contextMethods.getValues();
    for (const row of varRows) {
      if (!row.variableId || !row.value) continue;
      await onCreate({
        flowTestId,
        measuredCaptureId: Number(ctxData.measuredCaptureId),
        variableId: row.variableId,
        value: Number(row.value),
        measuredAt: ctxData.timeMode === "measuredAt" ? ctxData.measuredAt : undefined,
        testingTime: ctxData.timeMode === "testingTime" ? Number(ctxData.testingTime) : undefined,
        flag: row.flag || undefined,
        notes: row.notes || undefined,
      } as CreateFlowTestMeasurementRequest);
    }
    onCancel();
  };

  const timeDisplay = (() => {
    if (timeMode === "testingTime") {
      const value = watchCtx("testingTime");
      return value ? `${value} ${t("generic.units.minutes")}` : "";
    }
    const measuredAt = watchCtx("measuredAt");
    return measuredAt ? formatTimestamp(measuredAt) : "";
  })();

  return (
    <Paper variant="outlined" sx={{ p: 3, m: 2 }}>
      {step === "time" ? (
        <FormProvider {...contextMethods}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle2">
              {t("flow-tests.flow-test-measurement.step1-title")}
            </Typography>

            <Grid2 container spacing={2} alignItems="flex-start">
              <Grid2 size={{ xs: 12, md: 5 }}>
                <PaginatedAutocomplete
                  name="measuredCaptureId"
                  label={`${t("flow-tests.flow-test-measurement.measured-capture")} *`}
                  fetchFn={fetchCaptures}
                  fetchOneFn={fetchOneCapture}
                  isEditing={true}
                  isSaving={false}
                  formStyles={{ field: { width: "100%" } }}
                  limit={50}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label={`${t("flow-tests.flow-test-measurement.time-type")} *`}
                  {...registerCtx("timeMode")}
                  value={timeMode}
                  onChange={(e) =>
                    contextMethods.setValue("timeMode", e.target.value as "measuredAt" | "testingTime")
                  }
                >
                  <MenuItem value="testingTime">
                    {t("flow-tests.flow-test-measurement.testing-time")}
                  </MenuItem>
                  <MenuItem value="measuredAt">
                    {t("flow-tests.flow-test-measurement.measured-at")}
                  </MenuItem>
                </TextField>
              </Grid2>

              {timeMode === "testingTime" ? (
                <Grid2 size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="X"
                    {...registerCtx("testingTime")}
                    error={!!contextMethods.formState.errors.testingTime}
                    helperText={contextMethods.formState.errors.testingTime?.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {t("generic.units.minutes")}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid2>
              ) : (
                <Grid2 size={{ xs: 12, md: 3 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="measuredAt"
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          label="DD/MM/YYYY, HH:mm"
                          value={field.value ? dayjs(field.value, "YYYY-MM-DD HH:mm:ss") : null}
                          format="DD/MM/YYYY, HH:mm"
                          onChange={(date) =>
                            field.onChange(date ? date.format("YYYY-MM-DD HH:mm:ss") : null)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!contextMethods.formState.errors.measuredAt,
                              helperText: contextMethods.formState.errors.measuredAt?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid2>
              )}
            </Grid2>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleStep1Next}
                disabled={
                  !measuredCapture ||
                  (timeMode === "testingTime" ? !watchCtx("testingTime") : !watchCtx("measuredAt"))
                }
              >
                {t("flow-tests.flow-test-measurement.next-variables")}
              </Button>
              <Button size="small" onClick={onCancel}>
                {t("generic.cancel")}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2">
              {t("flow-tests.flow-test-measurement.step2-title")} -{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                {timeDisplay}
              </Typography>
            </Typography>

            <Button
              size="small"
              onClick={() => setStep("time")}
              sx={{
                py: 0.5,
                px: 1,
                minHeight: 28,
                lineHeight: 1,
                "& .MuiButton-startIcon": {
                  marginRight: 0.5,
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <KeyboardArrowLeft fontSize="small" />
              {t("generic.back")}
            </Button>
          </Box>

          <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "25%" }}>{t("flow-tests.flow-test-measurement.variable")} *</TableCell>
                <TableCell sx={{ width: "15%" }}>{t("flow-tests.flow-test-measurement.flag")}</TableCell>
                <TableCell sx={{ width: "20%" }}>{t("flow-tests.flow-test-measurement.value")} *</TableCell>
                <TableCell sx={{ width: "40%" }}>{t("flow-tests.flow-test-measurement.notes")}</TableCell>
                <TableCell sx={{ width: 50 }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {varRows.map((row, idx) => (
                <TableRow key={idx} sx={{ verticalAlign: "top" }}>
                  <TableCell sx={{ width: "25%" }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={row.variableId ?? ""}
                      error={rowErrors[idx]?.variableId}
                      onChange={(e) => updateVarRow(idx, "variableId", Number(e.target.value))}
                    >
                      {variables.map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell sx={{ width: "15%" }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.flag}
                      onChange={(e) => updateVarRow(idx, "flag", e.target.value)}
                    />
                  </TableCell>

                  <TableCell sx={{ width: "20%" }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={row.value}
                      error={rowErrors[idx]?.value}
                      onChange={(e) => updateVarRow(idx, "value", e.target.value)}
                    />
                  </TableCell>

                  <TableCell sx={{ width: "40%" }}>
                    <TextField
                      fullWidth
                      multiline
                      size="small"
                      value={row.notes}
                      onChange={(e) => updateVarRow(idx, "notes", e.target.value)}
                    />
                  </TableCell>

                  <TableCell sx={{ width: 50 }}>
                    {varRows.length > 1 && (
                      <IconButton size="small" onClick={() => removeVarRow(idx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button size="small" startIcon={<Add />} onClick={addVarRow}>
              {t("generic.add-row")}
            </Button>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
            >
              {t("generic.save-all")}
            </Button>

            <Button size="small" onClick={onCancel}>
              {t("generic.cancel")}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};